import React from 'react';
import { Mail, Loader2, ExternalLink } from 'lucide-react';
import { moduleApi } from '../../lib/api';
import { MessageFormatBar } from '../ui/MessageFormatBar';
import { TokenPicker, TokenDef } from '../ui/TokenPicker';
import { DemarchesSuivantesPicker } from '../Demandes/DemarchesSuivantesPicker';
import { DestinataireEmail } from '../ui/DestinataireEmail';

const JETONS: TokenDef[] = [
    { token: '{{prenom}}', desc: 'Prénom du client' },
    { token: '{{nom}}', desc: 'Nom (ou raison sociale) du client' },
    { token: '{{entreprise}}', desc: 'Votre établissement' },
    { token: '{{logo}}', desc: 'Votre logo' },
    { token: '{{bouton}}', desc: 'Bouton vers la Démarche (sinon ajouté en fin de message)' },
];

export interface PlaquetteEmail {
    send_email: boolean;
    email_subject: string;
    email_message: string;
    demarche_template_id: string;
    // Démarches que le client enchaînera après la première, dans cet ordre (facultatif).
    demarche_template_ids_suivants: string[];
}

/**
 * Le message qui accompagne la plaquette, et la Démarche vers laquelle le client sera renvoyé.
 *
 * Demande d'Emeni (2026-08-26) : « après prise de connaissance de la plaquette ils peuvent cliquer sur un
 * bouton qui les renvoie vers la démarche client de mon choix ». Le client suit ce bouton, répond aux
 * questions, prend son rendez-vous — et le RDV Pro qui en naît porte déjà ses réponses, sans ressaisie.
 *
 * La mise en forme est celle de tous les autres emails de l'application : rien de nouveau à apprendre, et
 * le rendu final est identique.
 */
export function PlaquetteEmailFields({ valeur, onChange, destinataire, destinataireEstReferent }: {
    valeur: PlaquetteEmail;
    onChange: (v: PlaquetteEmail) => void;
    /** À qui le mail partira réellement — le référent prime sur l'entreprise (cf. DestinataireEmail). */
    destinataire?: string;
    destinataireEstReferent?: boolean;
}) {
    const [demarches, setDemarches] = React.useState<any[] | null>(null);
    const messageRef = React.useRef<HTMLTextAreaElement>(null);
    const modifier = (patch: Partial<PlaquetteEmail>) => onChange({ ...valeur, ...patch });

    React.useEffect(() => {
        moduleApi.getDocumentTemplates()
            .then((r: any) => setDemarches(Array.isArray(r) ? r : []))
            .catch(() => setDemarches([]));
    }, []);

    // Seules les Démarches adressées à un CLIENT et actives ont un sens ici : un sondage salarié ou un
    // modèle désactivé n'a rien à faire dans une offre commerciale.
    const utilisables = (demarches || []).filter((d) => d.audience !== 'employe' && d.is_active !== false);
    const choisie = utilisables.find((d) => d.id === valeur.demarche_template_id);

    return (
        <div className="space-y-3 border-t border-border pt-3">
            <label className="flex items-start gap-2.5 cursor-pointer">
                <input type="checkbox" checked={valeur.send_email} onChange={(e) => modifier({ send_email: e.target.checked })}
                    className="accent-brand-600 w-4 h-4 mt-0.5" />
                <span>
                    <span className="text-xs font-bold text-content flex items-center gap-1.5"><Mail size={13} /> Envoyer la plaquette au client par email</span>
                    <span className="block text-[11px] text-content-muted mt-0.5">
                        Sans cette case, la plaquette est seulement conservée sur le dossier — utile si vous
                        l'avez déjà envoyée depuis votre messagerie.
                    </span>
                </span>
            </label>

            {valeur.send_email && (
                <div className="space-y-3 pl-1">
                    <DestinataireEmail email={destinataire} estReferent={destinataireEstReferent} />
                    <div className="space-y-1">
                        <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Objet</label>
                        <input value={valeur.email_subject} onChange={(e) => modifier({ email_subject: e.target.value })}
                            placeholder="Notre offre de privatisation"
                            className="w-full bg-surface border border-border rounded-lg px-2.5 py-1.5 text-sm text-content outline-none focus:border-brand-500" />
                    </div>

                    <div className="space-y-1.5">
                        <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Message</label>
                        <MessageFormatBar textareaRef={messageRef} value={valeur.email_message} onChange={(v) => modifier({ email_message: v })} />
                        <textarea ref={messageRef} value={valeur.email_message} rows={6}
                            onChange={(e) => modifier({ email_message: e.target.value })}
                            placeholder={"Bonjour {{prenom}},\n\nVous trouverez notre plaquette en pièce jointe.\n\n{{bouton}}"}
                            className="w-full bg-surface border border-border rounded-lg px-2.5 py-2 text-sm text-content outline-none focus:border-brand-500 resize-y" />
                        <TokenPicker tokens={JETONS} textareaRef={messageRef} value={valeur.email_message} onChange={(v) => modifier({ email_message: v })} />
                    </div>

                    <div className="space-y-1">
                        <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest">
                            Démarche vers laquelle le bouton renvoie
                        </label>
                        {demarches === null ? (
                            <p className="text-[11px] text-content-muted flex items-center gap-1.5"><Loader2 size={11} className="animate-spin" /> Chargement...</p>
                        ) : utilisables.length === 0 ? (
                            <p className="text-[11px] text-amber-600 dark:text-amber-400">
                                Aucune Démarche client disponible — créez-en une dans Paramètres &gt; Démarches.
                            </p>
                        ) : (
                            <>
                                <select value={valeur.demarche_template_id}
                                    onChange={(e) => modifier({
                                        demarche_template_id: e.target.value,
                                        // La Démarche promue principale ne peut pas rester dans la suite.
                                        demarche_template_ids_suivants: (valeur.demarche_template_ids_suivants || []).filter((id) => id !== e.target.value),
                                    })}
                                    className="w-full bg-surface border border-border rounded-lg px-2.5 py-1.5 text-sm text-content outline-none focus:border-brand-500">
                                    <option value="">— Choisir —</option>
                                    {utilisables.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                                </select>
                                {/* On dit clairement si la Démarche choisie propose un rendez-vous : c'est toute
                                    la différence entre récolter des réponses et décrocher un RDV Pro. */}
                                {choisie && (
                                    <p className="text-[11px] text-content-muted flex items-start gap-1.5 mt-1">
                                        <ExternalLink size={11} className="mt-0.5 shrink-0" />
                                        {choisie.offer_appointment
                                            ? "Le client répondra aux questions puis choisira un créneau. Vous recevrez son rendez-vous dans RDV Pro, avec ses réponses attachées."
                                            : "Cette Démarche ne propose pas de créneau : le client répondra aux questions, sans prise de rendez-vous."}
                                    </p>
                                )}
                            </>
                        )}
                    </div>

                    <DemarchesSuivantesPicker
                        demarches={utilisables}
                        principalId={valeur.demarche_template_id}
                        valeur={valeur.demarche_template_ids_suivants || []}
                        onChange={(ids) => modifier({ demarche_template_ids_suivants: ids })}
                    />
                </div>
            )}
        </div>
    );
}
